import { Box } from '@material-ui/core';
import { PaymentMethodType } from '@nemuru-eng/js-api-client';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCheckoutSessionContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/contexts/CheckoutSessionContext';
import { NextActions } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/components/NextActions/NextActions';
import { CreditLinePaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/CreditLine/_shared/entities/PaymentMethodDetail/CreditLinePaymentMethodDetailEntity';
import { ConditionsAndProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/CreditLine/ConditionsAndProgress/ConditionsAndProgress';
import { useLoanPaymentIntent } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/hooks/useLoanPaymentIntent';
import {
  NextActionFactory,
  NextActionFactoryProps,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/NextActions/components/NextActionFactory';
import { MerchantPosDialog } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosDialog';
import { CustomSkeleton } from '@/components/shared/components/CustomSkeleton';

interface CreditLinePaymentMethodProps {
  paymentMethodType: PaymentMethodType;
}

export const CreditLinePaymentMethod = ({ paymentMethodType }: CreditLinePaymentMethodProps) => {
  const { paymentMethodDetailEntities } = useCheckoutSessionContext();

  const { refreshPaymentMethodEntity } = useLoanPaymentIntent();

  const [loading, setLoading] = useState(true);
  const [merchantPosOpened, setMerchantPosOpened] = useState(false);

  const openMerchantPos = useCallback(() => setMerchantPosOpened(true), []);

  const LoanNextActionFactory = useMemo(
    () => (props: Omit<NextActionFactoryProps, 'onOpenMerchantPos'>) =>
      <NextActionFactory {...props} onOpenMerchantPos={openMerchantPos} />,
    [openMerchantPos],
  );

  const paymentMethodDetailEntity = useMemo(
    () =>
      paymentMethodDetailEntities.find((pme) => pme.type === paymentMethodType) as
        | CreditLinePaymentMethodDetailEntity
        | undefined,
    [paymentMethodDetailEntities, paymentMethodType],
  );

  useEffect(() => {
    if (!paymentMethodDetailEntity) return;
    setLoading(false);
  }, [paymentMethodDetailEntity]);

  useEffect(() => {
    const refresh = async () => {
      if (
        paymentMethodDetailEntity &&
        paymentMethodDetailEntity.isSelected === false &&
        paymentMethodDetailEntity.isRefreshed === false &&
        paymentMethodDetailEntities.length > 0
      ) {
        await refreshPaymentMethodEntity(paymentMethodDetailEntity);
      }
    };

    refresh();
  }, [paymentMethodDetailEntity]);

  const creditLinePaymentMethodDetailEntity =
    paymentMethodDetailEntity instanceof CreditLinePaymentMethodDetailEntity ? paymentMethodDetailEntity : undefined;

  const showSkeletons =
    !creditLinePaymentMethodDetailEntity || !creditLinePaymentMethodDetailEntity.isRefreshed || loading;

  return (
    <Box display="flex" flexDirection="column" gridGap={24}>
      {showSkeletons ? (
        <>
          <CustomSkeleton variant="rect" width="100%" height="268px" />
          <CustomSkeleton variant="rect" width="100%" height="295px" />
        </>
      ) : (
        <>
          <ConditionsAndProgress
            paymentMethodDetailEntity={creditLinePaymentMethodDetailEntity}
            onOpenMerchantPos={openMerchantPos}
          />
          <NextActions
            paymentMethodDetailEntity={creditLinePaymentMethodDetailEntity}
            NextActionFactory={LoanNextActionFactory}
          />
        </>
      )}
      {creditLinePaymentMethodDetailEntity ? (
        <MerchantPosDialog
          opened={merchantPosOpened}
          setOpened={setMerchantPosOpened}
          paymentMethodDetailEntity={creditLinePaymentMethodDetailEntity}
        />
      ) : null}
    </Box>
  );
};
